import * as React from 'react';
import type { ReactNode } from 'react';
import { Button } from '../primitives/Button';

/* Shared body for the confirmation-style dialogs (useConfirm,
   confirmDialog, alertDialog). Extracted because the imperative
   and declarative paths had ~50 lines of duplicated JSX — padding,
   button bar, destructive variant handling — that used to drift
   silently between the two surfaces. Any future visual change to
   the confirm UI lands here once. */
export interface ConfirmBodyProps {
  body?: ReactNode;
  /* null omits the cancel button entirely (alertDialog case). */
  cancelLabel?: string | null;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /* Optional id assigned to the wrapper div — used by useConfirm
     to wire the Modal's aria-describedby pointer at the body text
     for the APG alertdialog pattern. */
  id?: string;
}

const WRAPPER_STYLE = {
  padding: 'var(--sp-lg) var(--sp-xl) var(--sp-xl)',
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--type-compact-body)',
  color: 'var(--fg-2)',
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap' as const,
};

const BUTTON_ROW_STYLE = {
  display: 'flex' as const,
  justifyContent: 'flex-end' as const,
  gap: 'var(--sp-sm)',
  marginBlockStart: 'var(--sp-lg)',
};

export function ConfirmBody({
  body,
  cancelLabel,
  confirmLabel,
  destructive = false,
  onConfirm,
  onCancel,
  id,
}: ConfirmBodyProps) {
  return (
    <div style={WRAPPER_STYLE}>
      {/* `id` lands on a body-ONLY inner wrapper, NOT on the outer
          div, so a Modal's aria-describedby pointing at this id
          references only the message text (not the Cancel/Confirm
          buttons too) — codex Modal r1 flagged the accessible-
          description-includes-action-labels regression. */}
      {body != null && (
        <div id={id} data-jkit-confirm-body="true">
          {body}
        </div>
      )}
      <div style={BUTTON_ROW_STYLE}>
        {cancelLabel != null && (
          <Button variant="ghost" onClick={onCancel}>
            {cancelLabel}
          </Button>
        )}
        <Button variant={destructive ? 'destructive' : 'primary'} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}
